<script lang="ts" setup>
import { computed, onMounted, ref, watch } from '#imports';
import { useService } from '@kaokei/use-vue-service';
import HTLinkButton from '~/components/HTLinkButton.vue';
import UploadAvatar from '~/components/UploadAvatar.vue';
import { ProfileService } from '~/services/profile.service';

const form1Ref = ref();
const cropRef = ref();
const service = useService(ProfileService);

function getForm1Ref() {
  return form1Ref.value;
}
const modalWidth = computed(() => {
  return service.cropping ? '368px' : '468px';
});
const switchNickname = () => {
  form1Ref.value.clearValidate();
  service.generateNewUserInfo(true);
};

const skip = () => {
  service.skip();
};

const setCropping = (cropping: boolean) => {
  service.cropping = cropping;
};

watch(() => service.visible, (visible) => {
  if (visible) {
    service.generateNewUserInfo();
  }
}, { immediate: true });

onMounted(() => {
  service.init(getForm1Ref);
});

</script>

<template>
  <a-modal
    v-model:open="service.visible"
    :width="modalWidth"
    :footer="null"
    :closable="false"
    :mask-closable="false"
    :destroy-on-close="true"
    :body-style="{ padding: '0' }"
    :centered="true"
  >
    <div v-show="service.cropping" ref="cropRef"></div>
    <div v-show="!service.cropping" class="h-[372px] pt-[18px] text-center">
      <h6 class="text-base font-bold text-colorText">欢迎加入核桃OJ</h6>
      <p class="mt-2 py-[1px] text-sm text-colorTextTertiary">
        你已完成注册，添加头像和昵称，让更多人认识你吧
      </p>
      <HTLinkButton
        class="absolute right-[17px] top-[17px] min-w-11 cursor-pointer px-1 text-sm leading-6 !text-colorTextTertiary"
        @click="skip"
        >跳过</HTLinkButton
      >
      <div class="form-wrapper mt-6">
        <a-form
          ref="form1Ref"
          layout="vertical"
          autocomplete="off"
          :model="service.model"
          :rules="service.rules"
        >
          <a-form-item name="avatar" class="!mb-8 text-center">
            <UploadAvatar
              v-model="service.model.avatar"
              :avatar-size="100"
              :hover-text="'上传头像'"
              :get-crop-container="() => cropRef"
              @crop-opened="setCropping(true)"
              @crop-closed="setCropping(false)"
            />
          </a-form-item>

          <a-form-item class="nickname-item relative" name="nickname">
            <a-input
              v-model:value.trim="service.model.nickname"
              size="large"
              :class="{ 'reach-max': service.model.nickname.length >= 15 }"
              :maxlength="15"
              placeholder="输入昵称"
              autocomplete="off"
              label="昵称"
              autofocus
              show-count
            />
            <a-button
              size="large"
              type="link"
              class="!absolute right-0 top-0 z-10 !text-sm !text-colorTextSecondary"
              :loading="service.generating ? { delay: 300 } : false"
              @click="switchNickname"
              >换一个</a-button
            >
          </a-form-item>

          <a-form-item>
            <a-button
              type="primary"
              class="!h-[40px] !font-bold"
              block
              :loading="service.loading"
              @click="service.handleSubmit"
            >
              完成
            </a-button>
          </a-form-item>
        </a-form>
      </div>
    </div>
  </a-modal>
</template>

<style scoped>
.form-wrapper {
  @apply px-20;
}
.reach-max :deep(.ant-input-show-count-suffix) {
  color: #f53f3f;
}
:deep(.nickname-item .ant-input-suffix) {
  font-size: 14px;
}
.nickname-item :deep(.ant-input-suffix) {
  @apply pr-[50px];
}
:deep(.ant-form-item .ant-form-item-explain-error) {
  @apply text-left text-xs leading-6;
}
:deep(
  .ant-input-affix-wrapper-status-error:not(
      .ant-input-affix-wrapper-disabled
    ):not(.ant-input-affix-wrapper-borderless).ant-input-affix-wrapper
) {
  border-color: #ff7d00 !important;
}
:deep(.ant-form-item-explain-error) {
  color: #ff7d00 !important;
}
</style>
